import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useInView } from "react-intersection-observer";
import createProduct from "../../api/createProduct";
import deleteProduct from "../../api/deleteProduct";
import editProduct from "../../api/editProduct";
import useGetAllProducts from "../../hooks/useGetAllProducts";
import useGetProductByCategory from "../../hooks/useGetProductsByCategory";
import Spinner from "../spinner/Spinner";
import { ProductProps } from "../../types/ComponentProps";
import ProductListStyles from "./ProductListStyles.module.css";
import CreateProductModal from "./CreateProductModal";
import DeleteProductModal from "./DeleteProductModal";
import UpdateProductModal from "./UpdateProductModal";

// Modal State Type
type ModalState = {
  type: "create" | "edit" | "delete" | null;
  productId?: number;
  productCategory?: "Merch" | "Voucher" | null;
};

// Categories matching the shop page
const categories: { label: string; value: string }[] = [
  { label: "All", value: "All" },
  { label: "Apparel & Company Merch", value: "Apparel & Company Merch" },
  { label: "Drugstores", value: "Drugstores" },
  { label: "Food & Dining", value: "Food & Dining" },
  { label: "Malls & Groceries", value: "Malls & Groceries" },
  { label: "Retail & Shopping", value: "Retail & Shopping" },
  { label: "Sweets", value: "Sweets" },
  { label: "Travel & Experiences", value: "Travel & Experiences" },
  { label: "Wellness & Personal Care", value: "Wellness & Personal Care" },
];

// Product types
const productTypes: { label: string; value: string }[] = [
  { label: "All Types", value: "All" },
  { label: "Merch", value: "Merch" },
  { label: "Voucher", value: "Voucher" },
];

const ProductList: React.FC = () => {
  const queryClient = useQueryClient();
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const { ref, inView } = useInView();

  // Filter states
  const [searchInput, setSearchInput] = useState("");
  const [activeCategory, setActiveCategory] = useState("All");
  const [activeType, setActiveType] = useState("All");
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);
  const categoryDropdownRef = useRef<HTMLDivElement | null>(null);
  const typeDropdownRef = useRef<HTMLDivElement | null>(null);

  const {
    getAllProductsData,
    isLoadingGetAllProducts,
    fetchNextPageGetAllProducts,
    hasNextPageGetAllProducts,
    isFetchingNextPageGetAllProducts,
  } = useGetAllProducts();

  const {
    getProductsByCategoryData,
    isLoadingGetProductsByCategory,
    fetchNextPageGetProductsByCategory,
    hasNextPageGetProductsByCategory,
    isFetchingNextPageGetProductsByCategory,
  } = useGetProductByCategory(activeCategory !== "All" ? activeCategory : "");

  const [activeDropdown, setActiveDropdown] = useState<number | null>(null);
  const [modalState, setModalState] = useState<ModalState>({ type: null });
  const [isRefetching, setIsRefetching] = useState(false);
  const [toggleState, setToggleState] = useState<boolean>(false);
  const [buttonCreateDropDown, setButtonCreateDropDown] = useState(false);

  const [createProductForm, setCreateProductForm] = useState({
    productName: "",
    type: "",
    productImage: null as File | null,
    price: null as number | null,
    quantity: null as number | null,
    addOns: [],
    variant: [],
    category: "",
    productDescription: "",
  });

  const [createProductIsLoading, setCreateProductIsLoading] = useState(false);
  const [errorHandler, setErrorHandler] = useState({
    success: true,
    message: "",
    field: "",
  });

  // Track active index for keyboard navigation in dropdowns
  const [createDropdownIndex, setCreateDropdownIndex] = useState(0);
  const [actionDropdownIndex, setActionDropdownIndex] = useState(0);
  const createDropdownItemsRef = useRef<(HTMLButtonElement | null)[]>([]);
  const actionDropdownItemsRef = useRef<(HTMLButtonElement | null)[]>([]);

  useEffect(() => {
    if (inView) {
      if (activeCategory === "All") {
        if (hasNextPageGetAllProducts) {
          fetchNextPageGetAllProducts();
        }
      } else {
        if (hasNextPageGetProductsByCategory) {
          fetchNextPageGetProductsByCategory();
        }
      }
    }
  }, [inView, activeCategory, fetchNextPageGetAllProducts, hasNextPageGetAllProducts, fetchNextPageGetProductsByCategory, hasNextPageGetProductsByCategory]);

  // Close filter dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        categoryDropdownRef.current &&
        !categoryDropdownRef.current.contains(event.target as Node)
      ) {
        setShowCategoryDropdown(false);
      }
      if (
        typeDropdownRef.current &&
        !typeDropdownRef.current.contains(event.target as Node)
      ) {
        setShowTypeDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setButtonCreateDropDown(false);
      }
    };

    if (buttonCreateDropDown) {
      document.addEventListener("mousedown", handleClickOutside);
      // Focus first item when dropdown opens
      setTimeout(() => {
        createDropdownItemsRef.current[0]?.focus();
      }, 0);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [buttonCreateDropDown]);

  // Focus first action item when action dropdown opens
  useEffect(() => {
    if (activeDropdown !== null) {
      setActionDropdownIndex(0);
      // Use a longer delay to ensure buttons are rendered and refs are populated
      setTimeout(() => {
        if (actionDropdownItemsRef.current[0]) {
          actionDropdownItemsRef.current[0].focus();
        }
      }, 100);
    } else {
      // Reset refs when dropdown closes
      actionDropdownItemsRef.current = [];
    }
  }, [activeDropdown]);

  // Handle keyboard navigation for create dropdown
  const handleCreateDropdownKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const items = createDropdownItemsRef.current.filter(Boolean);
      const itemCount = items.length;

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setCreateDropdownIndex((prev) => {
            const next = (prev + 1) % itemCount;
            items[next]?.focus();
            return next;
          });
          break;
        case "ArrowUp":
          e.preventDefault();
          setCreateDropdownIndex((prev) => {
            const next = (prev - 1 + itemCount) % itemCount;
            items[next]?.focus();
            return next;
          });
          break;
        case "Escape":
          e.preventDefault();
          setButtonCreateDropDown(false);
          break;
        case "Tab":
          setButtonCreateDropDown(false);
          break;
      }
    },
    []
  );

  // Handle keyboard navigation for action dropdown
  const handleActionDropdownKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const items = actionDropdownItemsRef.current.filter(Boolean);
      const itemCount = items.length;

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setActionDropdownIndex((prev) => {
            const next = (prev + 1) % itemCount;
            items[next]?.focus();
            return next;
          });
          break;
        case "ArrowUp":
          e.preventDefault();
          setActionDropdownIndex((prev) => {
            const next = (prev - 1 + itemCount) % itemCount;
            items[next]?.focus();
            return next;
          });
          break;
        case "Escape":
          e.preventDefault();
          setActiveDropdown(null);
          break;
        case "Tab":
          setActiveDropdown(null);
          break;
        case "Enter":
        case " ":
          // Let the button's native click handler work
          // Don't prevent default - allow the click to fire
          break;
      }
    },
    []
  );

  const truncateText = (text: string | undefined, limit: number) =>
    text && text.length > limit ? text.slice(0, limit) + "..." : text || "";

  // Get variant display for Merch products or quantity for Vouchers
  const getVariantDisplay = (product: ProductProps) => {
    if (product.type === "Merch" && product.variant && Array.isArray(product.variant)) {
      const sizeQuantities: string[] = [];
      product.variant.forEach((variant: any) => {
        if (variant.options && Array.isArray(variant.options)) {
          variant.options.forEach((option: any) => {
            if (option.title) {
              sizeQuantities.push(`${option.title}: ${option.quantity || 0}`);
            }
          });
        }
      });
      return sizeQuantities.length > 0 ? sizeQuantities.join(", ") : "No sizes";
    }
    return product.quantity?.toString() || "0";
  };

  // Filtered product list
  const productList = useMemo(() => {
    const pages =
      activeCategory === "All"
        ? getAllProductsData?.pages
        : getProductsByCategoryData?.pages;

    const flattened = pages?.flatMap((page: any) => page.products) || [];

    let list = [...flattened];

    // Filter by search input (product name)
    if (searchInput.trim()) {
      list = list.filter((product: ProductProps) =>
        product.productName?.toLowerCase().includes(searchInput.toLowerCase())
      );
    }

    // Filter by product type
    if (activeType !== "All") {
      list = list.filter((product: ProductProps) => product.type === activeType);
    }

    return list;
  }, [
    activeCategory,
    activeType,
    getAllProductsData?.pages,
    getProductsByCategoryData?.pages,
    searchInput,
  ]);

  const isLoadingProducts =
    activeCategory === "All"
      ? isLoadingGetAllProducts
      : isLoadingGetProductsByCategory;

  const isFetchingNextPage =
    activeCategory === "All"
      ? isFetchingNextPageGetAllProducts
      : isFetchingNextPageGetProductsByCategory;

  // React Query Mutations
  const createMutation = useMutation({
    mutationFn: () => {
      const hasInvalidVariant = createProductForm.variant.some(
        (variant: any) =>
          !variant.title.trim() ||
          variant.options.every((option: any) => !option.title.trim()),
      );

      if (hasInvalidVariant) {
        setErrorHandler({
          success: false,
          message:
            "Each variant must have a title and at least one valid option.",
          field: "variant",
        });
        return Promise.reject(
          new Error(
            "Each variant must have a title and at least one valid option.",
          ),
        );
      }

      return createProduct(createProductForm);
    },
    onSuccess: async () => {
      setIsRefetching(true);
      await queryClient.invalidateQueries({ queryKey: ["getAllProducts"] });
      setModalState({ type: null });
      setCreateProductIsLoading(false);
      setIsRefetching(false);
      setCreateProductForm({
        productName: "",
        type: "",
        productImage: null,
        price: null,
        quantity: null,
        addOns: [],
        variant: [],
        category: "",
        productDescription: "",
      });
    },
    onError: (error: Error) => {
      setIsRefetching(false);
      setCreateProductIsLoading(false);
      console.error("Error creating product:", error.message);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (productId: number) => deleteProduct(productId),
    onSuccess: async () => {
      setIsRefetching(true);
      await queryClient.invalidateQueries({ queryKey: ["getAllProducts"] });
      setModalState({ type: null });
      setCreateProductIsLoading(false);
      setIsRefetching(false);
      setCreateProductForm({
        productName: "",
        type: "",
        productImage: null,
        price: null,
        quantity: null,
        addOns: [],
        variant: [],
        category: "",
        productDescription: "",
      });
    },
    onError: (error: Error) => {
      setIsRefetching(false);
      setCreateProductIsLoading(false);
      console.error("Error deleting product:", error);
    },
  });

  const updateMutation = useMutation({
    mutationFn: (productId: number) =>
      editProduct(productId, createProductForm),
    onSuccess: async () => {
      setIsRefetching(true);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["getAllProducts"] }),
        queryClient.invalidateQueries({
          queryKey: ["getProductById", Number(modalState?.productId)],
        }),
      ]);
      handleCloseModal();
      setModalState({ type: null });
      setCreateProductIsLoading(false);
      setIsRefetching(false);
    },
    onError: (error: Error) => {
      setIsRefetching(false);
      setCreateProductIsLoading(false);
      console.error("Error creating product:", error.message);
    },
  });

  // Handlers
  const createProductSubmitHandler = () => {
    const { type, addOns, variant, quantity } = createProductForm;

    // Common required fields for all product types
    const requiredFieldsCommon: (keyof typeof createProductForm)[] = [
      "productImage",
      "productName",
      "type",
      "category",
      "productDescription",
      "price",
    ];

    // For Voucher, quantity is always required
    // For Merch, quantity is required ONLY if no variants are added
    const hasVariants = variant && Array.isArray(variant) && variant.length > 0 && 
      variant.some((v: any) => v.options && v.options.length > 0);
    
    const requiredFields = type === "Voucher" 
      ? [...requiredFieldsCommon, "quantity" as keyof typeof createProductForm]
      : requiredFieldsCommon;

    const missingField = requiredFields.find(
      (field) => !createProductForm[field] && createProductForm[field] !== 0,
    );

    if (missingField) {
      setErrorHandler({
        success: false,
        message: `${missingField.replace(/([A-Z])/g, " $1")} is required.`,
        field: missingField,
      });
      return;
    }

    // For Merch without variants, quantity is required
    if (type === "Merch" && !hasVariants && (quantity === null || quantity === undefined)) {
      setErrorHandler({
        success: false,
        message: "Quantity is required when no variants are added.",
        field: "quantity",
      });
      return;
    }

    // Validate price is greater than 0
    if (createProductForm.price !== null && createProductForm.price <= 0) {
      setErrorHandler({
        success: false,
        message: "Price must be greater than 0.",
        field: "price",
      });
      return;
    }

    if (toggleState && addOns.length === 0) {
      setErrorHandler({
        success: false,
        message: "You must select an Add-on.",
        field: "addOns",
      });
      return;
    }

    // Reset error state and proceed with submission
    setErrorHandler({ success: true, message: "", field: "" });
    setCreateProductIsLoading(true);
    setToggleState(false);
    createMutation.mutate();
  };

  const updateProductionSubmitHandler = () => {
    const { type, variant, quantity } = createProductForm;
    
    // Check if product has valid variants
    const hasVariants = variant && Array.isArray(variant) && variant.length > 0 && 
      variant.some((v: any) => v.options && v.options.length > 0);

    // Base required fields for all products
    const baseRequiredFields: Array<keyof typeof createProductForm> = [
      "productName",
      "type",
      "category",
      "price",
      "productDescription",
    ];

    // Vouchers always need quantity at product level
    // Merch needs quantity only if no variants are defined
    const requiredFields = type === "Voucher" 
      ? [...baseRequiredFields, "quantity" as keyof typeof createProductForm]
      : baseRequiredFields;

    for (const field of requiredFields) {
      const value = createProductForm[field];
      // Allow 0 as valid for quantity fields
      if (value === null || value === undefined || value === "") {
        setErrorHandler({
          success: false,
          message: `${field.replace(/([A-Z])/g, " $1")} is required.`,
          field,
        });
        return;
      }
    }

    // For Merch without variants, quantity is required
    if (type === "Merch" && !hasVariants && (quantity === null || quantity === undefined)) {
      setErrorHandler({
        success: false,
        message: "Quantity is required when no variants are added.",
        field: "quantity",
      });
      return;
    }

    // Validate price is greater than 0
    if (createProductForm.price !== null && createProductForm.price <= 0) {
      setErrorHandler({
        success: false,
        message: "Price must be greater than 0.",
        field: "price",
      });
      return;
    }

    if (!modalState.productId) return;
    updateMutation.mutate(modalState?.productId);
    setErrorHandler({ success: true, message: "", field: "" });
    setCreateProductIsLoading(true);
  };

  const onDeleteSubmitHandler = () => {
    if (modalState.type === "delete" && modalState.productId) {
      setCreateProductIsLoading(true);
      deleteMutation.mutate(modalState.productId);
    }
  };

  const onShowDropDownUpdateHandler = (productId: number) => {
    setActiveDropdown(activeDropdown === productId ? null : productId);
  };

  const handleCloseModal = () => {
    setCreateProductForm({
      productName: "",
      type: "",
      productImage: null as File | null,
      price: null as number | null,
      quantity: null as number | null,
      addOns: [],
      variant: [],
      category: "",
      productDescription: "",
    });
    setActiveDropdown(null);
    setToggleState(false);
    setErrorHandler({ success: true, message: "", field: "" });
  };

  return (
    <div className={ProductListStyles.manageProductListBlk}>
      {/* Modals */}
      {modalState.type === "create" && (
        <CreateProductModal
          createProductSubmitHandler={createProductSubmitHandler}
          createProductIsLoading={createProductIsLoading}
          createProductForm={createProductForm}
          inputChangeHandler={(e) =>
            setCreateProductForm((prev) => ({
              ...prev,
              [e.target.name]: e.target.value,
            }))
          }
          onCloseClickHandler={() => {
            handleCloseModal();
            setActiveDropdown(null);
            setModalState({ type: null });
          }}
          errorHandler={errorHandler}
          setCreateProductForm={setCreateProductForm}
          toggleState={toggleState}
          setToggleState={setToggleState}
        />
      )}

      {modalState.type === "delete" && modalState.productId && (
        <DeleteProductModal
          setShowDeleteModal={() => setModalState({ type: null })}
          onDeleteSubmitHandler={onDeleteSubmitHandler}
          createProductIsLoading={createProductIsLoading}
        />
      )}

      {modalState.type === "edit" && modalState.productId && (
        <UpdateProductModal
          activeProductId={modalState.productId}
          productId={modalState.productId}
          productType={modalState.productCategory || "Merch"}
          updateProductionSubmitHandler={updateProductionSubmitHandler}
          createProductSubmitHandler={createProductSubmitHandler}
          createProductIsLoading={createProductIsLoading}
          createProductForm={createProductForm}
          inputChangeHandler={(e: any) =>
            setCreateProductForm((prev) => ({
              ...prev,
              [e.target.name]: e.target.value,
            }))
          }
          onCloseClickHandler={() => {
            handleCloseModal();
            setActiveDropdown(null);
            setModalState({ type: null });
          }}
          errorHandler={errorHandler}
          setCreateProductForm={setCreateProductForm}
          toggleState={toggleState}
          setToggleState={setToggleState}
        />
      )}

      {/* Header */}
      <div
        className={ProductListStyles.manageProductBlkTitle}
        style={
          getAllProductsData?.pages[0]?.products?.length > 10
            ? { overflowY: "scroll" }
            : {}
        }
      >
        <h1>Products</h1>

        <div
          style={{ marginLeft: "auto", position: "relative" }}
          ref={dropdownRef}
        >
          <button
            type="button"
            className={`${ProductListStyles.manageProductBlkTitleButton} ${
              buttonCreateDropDown ? ProductListStyles.buttonActive : ""
            }`}
            onClick={() => setButtonCreateDropDown((prev) => !prev)}
            aria-haspopup="menu"
            aria-expanded={buttonCreateDropDown}
            aria-controls="create-product-menu"
          >
            Add new product{" "}
            <i
              style={{ marginLeft: "5px" }}
              className={`fa-solid ${
                buttonCreateDropDown ? "fa-chevron-up" : "fa-chevron-down"
              }`}
              aria-hidden="true"
            />
          </button>
          {buttonCreateDropDown && (
            <div 
              id="create-product-menu"
              className={ProductListStyles.dropDownCreate}
              role="menu"
              aria-label="Product type options"
              onKeyDown={handleCreateDropdownKeyDown}
            >
              <button
                type="button"
                role="menuitem"
                ref={(el) => (createDropdownItemsRef.current[0] = el)}
                className={ProductListStyles.dropDownCreateItem}
                onClick={() => {
                  setModalState({ type: "create", productCategory: "Merch" });
                  setCreateProductForm(() => ({
                    type: "Merch",
                    productName: "",
                    productImage: null,
                    price: null,
                    quantity: null,
                    addOns: [],
                    variant: [],
                    category: "",
                    productDescription: "",
                  }));
                  setButtonCreateDropDown(false);
                }}
              >
                Merch
              </button>
              <button
                type="button"
                role="menuitem"
                ref={(el) => (createDropdownItemsRef.current[1] = el)}
                className={ProductListStyles.dropDownCreateItem}
                onClick={() => {
                  setModalState({ type: "create", productCategory: "Voucher" });
                  setCreateProductForm(() => ({
                    type: "Voucher",
                    productName: "",
                    productImage: null,
                    price: null,
                    quantity: null,
                    addOns: [],
                    variant: [],
                    category: "",
                    productDescription: "",
                  }));
                  setButtonCreateDropDown(false);
                }}
              >
                Voucher
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Filters Section */}
      <div className={ProductListStyles.filtersSection}>
        {/* Search Input */}
        <div className={ProductListStyles.searchInputWrapper}>
          <i className="fa-solid fa-magnifying-glass" aria-hidden="true"></i>
          <input
            type="text"
            placeholder="Search by product name"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            aria-label="Search products by name"
          />
          {searchInput && (
            <button
              type="button"
              className={ProductListStyles.clearSearchButton}
              onClick={() => setSearchInput("")}
              aria-label="Clear search"
            >
              <i className="fa-solid fa-xmark" aria-hidden="true"></i>
            </button>
          )}
        </div>

        {/* Category Filter */}
        <div className={ProductListStyles.filterDropdownWrapper} ref={categoryDropdownRef}>
          <button
            type="button"
            className={ProductListStyles.filterButton}
            onClick={() => {
              setShowCategoryDropdown((prev) => !prev);
              setShowTypeDropdown(false);
            }}
            aria-haspopup="listbox"
            aria-expanded={showCategoryDropdown}
          >
            <span>Category: {activeCategory}</span>
            <i
              className={`fa-solid ${showCategoryDropdown ? "fa-chevron-up" : "fa-chevron-down"}`}
              aria-hidden="true"
            ></i>
          </button>
          {showCategoryDropdown && (
            <div
              className={ProductListStyles.filterDropdown}
              role="listbox"
              aria-label="Select category"
            >
              {categories.map((category) => (
                <button
                  key={category.value}
                  type="button"
                  role="option"
                  aria-selected={activeCategory === category.value}
                  className={`${ProductListStyles.filterDropdownItem} ${
                    activeCategory === category.value ? ProductListStyles.filterDropdownItemActive : ""
                  }`}
                  onClick={() => {
                    setActiveCategory(category.value);
                    setShowCategoryDropdown(false);
                  }}
                >
                  {category.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Type Filter */}
        <div className={ProductListStyles.filterDropdownWrapper} ref={typeDropdownRef}>
          <button
            type="button"
            className={ProductListStyles.filterButton}
            onClick={() => {
              setShowTypeDropdown((prev) => !prev);
              setShowCategoryDropdown(false);
            }}
            aria-haspopup="listbox"
            aria-expanded={showTypeDropdown}
          >
            <span>Type: {activeType === "All" ? "All Types" : activeType}</span>
            <i
              className={`fa-solid ${showTypeDropdown ? "fa-chevron-up" : "fa-chevron-down"}`}
              aria-hidden="true"
            ></i>
          </button>
          {showTypeDropdown && (
            <div
              className={ProductListStyles.filterDropdown}
              role="listbox"
              aria-label="Select product type"
            >
              {productTypes.map((type) => (
                <button
                  key={type.value}
                  type="button"
                  role="option"
                  aria-selected={activeType === type.value}
                  className={`${ProductListStyles.filterDropdownItem} ${
                    activeType === type.value ? ProductListStyles.filterDropdownItemActive : ""
                  }`}
                  onClick={() => {
                    setActiveType(type.value);
                    setShowTypeDropdown(false);
                  }}
                >
                  {type.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Clear Filters */}
        {(searchInput || activeCategory !== "All" || activeType !== "All") && (
          <button
            type="button"
            className={ProductListStyles.clearFiltersButton}
            onClick={() => {
              setSearchInput("");
              setActiveCategory("All");
              setActiveType("All");
            }}
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Table Headers */}
      <div className={ProductListStyles.productTableWrapper}>
        <table 
          className={ProductListStyles.productTable}
          aria-label="Products list"
        >
          <thead>
            <tr>
              <th scope="col">Product Image</th>
              <th scope="col">Product Name</th>
              <th scope="col">Type</th>
              <th scope="col">Category</th>
              <th scope="col">Price</th>
              <th scope="col">Variants/Qty</th>
              <th scope="col">Description</th>
              <th scope="col">Actions</th>
            </tr>
          </thead>
        </table>
      </div>

      {/* Product List */}
      <div className={ProductListStyles.productListMainContainer}>
        {isLoadingProducts || isRefetching ? (
          <div style={{ display: "block", padding: "10px 0" }}>
            <Spinner variant="medium-spinner" alignSpin="center-spinner" />
          </div>
        ) : productList.length === 0 ? (
          <p className={ProductListStyles.notAvailableBlkTxt}>
            {searchInput || activeCategory !== "All" || activeType !== "All"
              ? "No products match your filters"
              : "No products are currently available"}
          </p>
        ) : (
          <table 
            className={ProductListStyles.productTable}
            aria-label="Products"
          >
            <tbody>
              {productList.map((product: ProductProps, index: number) => (
                <tr key={product.productId || index}>
                  <td>
                    <img 
                      src={product.imageURL} 
                      alt={`${product.productName} product image`}
                      className={ProductListStyles.productImage}
                    />
                  </td>
                  <td>{truncateText(product.productName, 20)}</td>
                  <td>{product.type}</td>
                  <td>{product.category}</td>
                  <td>{product.price}</td>
                  <td>{getVariantDisplay(product)}</td>
                  <td>{truncateText(product.productDescription, 30)}</td>
                  <td>
                    <div className={ProductListStyles.actionsCell}>
                      {activeDropdown === Number(product.productId) && (
                        <div
                          className={ProductListStyles.dropDownEdit}
                          role="menu"
                          aria-label={`Actions for ${product.productName}`}
                        >
                          <button
                            type="button"
                            role="menuitem"
                            ref={(el) => (actionDropdownItemsRef.current[0] = el)}
                            className={ProductListStyles.dropDownEditItem}
                            onClick={() => {
                              const pid = product.productId ? Number(product.productId) : 0;
                              setActiveDropdown(null);
                              setModalState({
                                type: "edit",
                                productId: pid,
                                productCategory: (product.type as "Merch" | "Voucher") || "Merch",
                              });
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "ArrowDown") {
                                e.preventDefault();
                                actionDropdownItemsRef.current[1]?.focus();
                              } else if (e.key === "ArrowUp") {
                                e.preventDefault();
                                actionDropdownItemsRef.current[1]?.focus();
                              } else if (e.key === "Escape") {
                                e.preventDefault();
                                setActiveDropdown(null);
                              } else if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                const pid = product.productId ? Number(product.productId) : 0;
                                setActiveDropdown(null);
                                setModalState({
                                  type: "edit",
                                  productId: pid,
                                  productCategory: (product.type as "Merch" | "Voucher") || "Merch",
                                });
                              }
                            }}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            role="menuitem"
                            ref={(el) => (actionDropdownItemsRef.current[1] = el)}
                            className={ProductListStyles.dropDownEditItem}
                            onClick={() => {
                              setActiveDropdown(null);
                              setModalState({
                                type: "delete",
                                productId: Number(product.productId),
                              });
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "ArrowUp") {
                                e.preventDefault();
                                actionDropdownItemsRef.current[0]?.focus();
                              } else if (e.key === "ArrowDown") {
                                e.preventDefault();
                                actionDropdownItemsRef.current[0]?.focus();
                              } else if (e.key === "Escape") {
                                e.preventDefault();
                                setActiveDropdown(null);
                              } else if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                setActiveDropdown(null);
                                setModalState({
                                  type: "delete",
                                  productId: Number(product.productId),
                                });
                              }
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      )}
                      <button
                        type="button"
                        className={ProductListStyles.actionsButton}
                        onClick={() =>
                          onShowDropDownUpdateHandler(Number(product.productId))
                        }
                        aria-label={`Actions for ${product.productName}`}
                        aria-haspopup="menu"
                        aria-expanded={activeDropdown === Number(product.productId)}
                      >
                        <i className="fa-solid fa-ellipsis" aria-hidden="true"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div ref={ref} style={{ height: "1px" }}></div>
        {isFetchingNextPage && (
          <div style={{ display: "block", padding: "20px 0px" }}>
            <Spinner variant="medium-spinner" alignSpin="center-spinner" />
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductList;
